import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { useIsMobile } from '@/lib/useIsMobile'
import Avatar from '@/components/Avatar'
import { Send, Loader2, Hash, LogIn, Users as UsersIcon, Menu, Phone, MoreHorizontal, Search, Paperclip, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  format, formatDistanceToNow, isToday, isYesterday, differenceInMinutes, differenceInDays,
} from 'date-fns'
import { useCallManager } from '@/lib/callManager'
import ChannelSidebar, { type SidebarActiveRoute } from './ChannelSidebar'
import ChannelDirectory from './ChannelDirectory'
import StartDMModal from './StartDMModal'
import SearchMessagesModal from './SearchMessagesModal'
import { usePresence } from '@/lib/presence'
import { uploadMessageAttachment, deleteMessageAttachment, ATTACHMENT_ACCEPT_ATTR, MAX_ATTACHMENT_BYTES, type MessageAttachment } from '@/lib/messageAttachments'
import MessageAttachmentList from './MessageAttachments'
import ThreadDrawer from './ThreadDrawer'
import NewGroupModal from './NewGroupModal'
import MessageReactions, { type Reaction } from './MessageReactions'
import { parseMentions, renderWithMentions, insertMentionToken, removeMentionToken } from '@/lib/mentions'
import DraftMentionPills from '@/components/DraftMentionPills'

/**
 * Team chat — channels, direct messages, and group chats in one component.
 *
 * Route shapes (resolved via useParams):
 *   /app/team-chat/:channelName     →  channel mode (existing)
 *   /app/team-chat/dm/:dmId         →  DM mode
 *   /app/team-chat/group/:groupId   →  group mode
 *
 * The component is identical downstream of activeChannelId — polling,
 * realtime, mark-read, and send don't care what kind of conversation it
 * is. Only resolution (find the conv + verify membership + compute the
 * header) branches on route kind.
 *
 * For DMs and groups the header is computed from current participants
 * (not from a stored name field). Groups have name=NULL by design; DM
 * display name = the other participant's full_name.
 *
 * Channel-only behavior preserved: lazy-bootstrap of #general on first
 * visit; Join button for non-member access to a public channel. DMs and
 * groups can't be joined by non-members in v1 — typing the URL of a DM
 * you aren't in resolves to "not found" rather than offering a Join
 * affordance.
 */

interface TeamMessage {
  id: string
  team_conversation_id: string
  user_id: string | null
  content: string
  created_at: string
  edited_at: string | null
  deleted_at: string | null
  attachments: MessageAttachment[]
  parent_message_id: string | null
  profiles: { full_name: string | null } | null
}

const MAX_MESSAGES = 50
const POLL_MS = 5000
const MAX_CONTENT_LEN = 4000

function formatMessageTime(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const mins = differenceInMinutes(now, d)
  if (mins < 1) return 'just now'
  if (mins < 60) return formatDistanceToNow(d, { addSuffix: true })
  if (isToday(d)) return `Today at ${format(d, 'h:mm a')}`
  if (isYesterday(d)) return `Yesterday at ${format(d, 'h:mm a')}`
  if (differenceInDays(now, d) < 7) return `${format(d, 'EEE')} at ${format(d, 'h:mm a')}`
  return `${format(d, 'MMM d')} at ${format(d, 'h:mm a')}`
}

function formatGroupHeader(otherNames: string[]): string {
  if (otherNames.length === 0) return 'Group chat'
  if (otherNames.length <= 4) return otherNames.join(', ')
  const head = otherNames.slice(0, 4).join(', ')
  return `${head} +${otherNames.length - 4} more`
}

type ChannelState =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'not-member-channel'; channelId: string; channelName: string; description: string | null }
  | {
      kind: 'ready'
      channelId: string
      convType: 'channel' | 'dm' | 'group'
      displayName: string  // header title
      subtitle: string     // header subtitle
      participantCount: number
      avatarName?: string  // DM only — the other user's name, for the header avatar
      dmOtherId?: string   // DM only — the other participant's profile id, for call entry
    }

export default function TeamChat() {
  const { profile, workspace } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const params = useParams<{ channelName?: string; dmId?: string; groupId?: string }>()
  const isMobile = useIsMobile()
  const { onlineUserIds } = usePresence()
  const { state: callState, startCall, startGroupCall } = useCallManager()
  const callBusy = callState.kind !== 'idle' && callState.kind !== 'ended'
  // Mobile-only drawer state for the channel sidebar. Desktop ignores it
  // and renders the sidebar inline as today.
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  // Auto-close the drawer whenever the active conversation key changes
  // (i.e. the user picked a channel/DM/group) or the viewport crosses
  // back to desktop. Mirrors AppLayout's Phase-0 pattern.
  useEffect(() => {
    setMobileSidebarOpen(false)
  }, [params.channelName, params.dmId, params.groupId, isMobile])

  // Route shape detection: dm/group routes use distinct params, channel
  // route uses channelName (default 'general').
  const routeKind: 'channel' | 'dm' | 'group' =
    params.dmId ? 'dm' : params.groupId ? 'group' : 'channel'
  const routeKey: string =
    routeKind === 'dm' ? (params.dmId as string)
      : routeKind === 'group' ? (params.groupId as string)
      : (params.channelName || 'general').toLowerCase()

  const [channelState, setChannelState] = useState<ChannelState>({ kind: 'loading' })
  const [showDirectory, setShowDirectory] = useState(false)
  const [showStartDM, setShowStartDM] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [showNewGroup, setShowNewGroup] = useState(false)
  const [input, setInput] = useState('')
  // Bumped after a successful Join to force the resolution effect to re-run.
  const [resolveBump, setResolveBump] = useState(0)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // ─── Resolution ───────────────────────────────────────────────────────────
  // Re-runs whenever the route changes or after a join. Resets to loading
  // between switches so stale state doesn't flash.
  useEffect(() => {
    if (!workspace?.id || !profile?.id) return
    let cancelled = false
    setChannelState({ kind: 'loading' })

    ;(async () => {
      try {
        if (routeKind === 'channel') {
          const channelName = routeKey
          const { data: existing, error: findErr } = await supabase
            .from('team_conversations')
            .select('id, name, description')
            .eq('workspace_id', workspace.id)
            .eq('type', 'channel')
            .eq('name', channelName)
            .maybeSingle()
          if (findErr) throw findErr

          let channelId: string
          let description: string | null = null

          if (existing) {
            channelId = existing.id
            description = existing.description
          } else if (channelName === 'general') {
            const { data: created, error: cErr } = await supabase
              .from('team_conversations')
              .insert({
                workspace_id: workspace.id,
                type: 'channel',
                name: 'general',
                description: 'Default channel for everyone in the workspace',
                created_by: profile.id,
              })
              .select('id, description')
              .single()
            if (cErr) throw cErr
            channelId = created.id
            description = created.description

            const { data: members, error: mErr } = await supabase
              .from('profiles')
              .select('id')
              .eq('workspace_id', workspace.id)
            if (mErr) throw mErr
            if (members && members.length > 0) {
              const rows = members.map(m => ({ team_conversation_id: channelId, user_id: m.id }))
              const { error: seedErr } = await supabase
                .from('team_conversation_participants')
                .insert(rows)
              if (seedErr) throw seedErr
            }
          } else {
            if (!cancelled) setChannelState({ kind: 'not-found' })
            return
          }

          if (channelName === 'general') {
            await supabase
              .from('team_conversation_participants')
              .upsert(
                { team_conversation_id: channelId, user_id: profile.id },
                { onConflict: 'team_conversation_id,user_id', ignoreDuplicates: true },
              )
          }

          const { data: meRow, error: meErr } = await supabase
            .from('team_conversation_participants')
            .select('user_id')
            .eq('team_conversation_id', channelId)
            .eq('user_id', profile.id)
            .maybeSingle()
          if (meErr) throw meErr

          if (!meRow) {
            if (!cancelled) setChannelState({ kind: 'not-member-channel', channelId, channelName, description })
            return
          }

          const { count } = await supabase
            .from('team_conversation_participants')
            .select('user_id', { count: 'exact', head: true })
            .eq('team_conversation_id', channelId)

          if (!cancelled) {
            setChannelState({
              kind: 'ready',
              channelId,
              convType: 'channel',
              displayName: `#${channelName}`,
              subtitle: `${count || 0} ${(count || 0) === 1 ? 'member' : 'members'}${description ? ` · ${description}` : ''}`,
              participantCount: count || 0,
            })
          }
          return
        }

        // routeKind is 'dm' or 'group' from here on
        const expectedType: 'dm' | 'group' = routeKind
        const convId = routeKey

        const { data: conv, error: cErr } = await supabase
          .from('team_conversations')
          .select('id, type, workspace_id')
          .eq('id', convId)
          .eq('workspace_id', workspace.id)
          .eq('type', expectedType)
          .maybeSingle()
        if (cErr) throw cErr
        if (!conv) {
          if (!cancelled) setChannelState({ kind: 'not-found' })
          return
        }

        // Fetch all participants with their profile names so we can build
        // the header AND verify the current user's membership in one round-trip.
        const { data: parts, error: pErr } = await supabase
          .from('team_conversation_participants')
          .select('user_id, profiles!team_conversation_participants_user_fkey(id, full_name)')
          .eq('team_conversation_id', conv.id)
        if (pErr) throw pErr
        const partRows = (parts || []) as any[]
        const isMember = partRows.some(p => p.user_id === profile.id)
        if (!isMember) {
          // DMs and groups are not joinable by third parties. Surface as
          // not-found to avoid disclosing the conversation's existence.
          if (!cancelled) setChannelState({ kind: 'not-found' })
          return
        }

        // Keep id alongside name so the DM header can wire a "call this
        // person" button without a second round-trip. id comes straight
        // from p.user_id; profile.id is the embedded join's mirror.
        const others = partRows
          .filter(p => p.user_id !== profile.id)
          .map(p => ({
            id: p.user_id as string,
            fullName: (p.profiles?.full_name as string | null) || 'Unknown user',
          }))

        const displayName = expectedType === 'dm'
          ? (others[0]?.fullName || 'Direct message')
          : formatGroupHeader(others.map(o => o.fullName))
        const subtitle = expectedType === 'dm'
          ? 'Direct message'
          : `${partRows.length} members`

        if (!cancelled) {
          setChannelState({
            kind: 'ready',
            channelId: conv.id,
            convType: expectedType,
            displayName,
            subtitle,
            participantCount: partRows.length,
            avatarName: expectedType === 'dm' ? others[0]?.fullName : undefined,
            dmOtherId: expectedType === 'dm' ? others[0]?.id : undefined,
          })
        }
      } catch (err: any) {
        if (!cancelled) {
          console.warn('[team-chat] resolution failed:', err?.message)
          setChannelState({ kind: 'not-found' })
        }
      }
    })()

    return () => { cancelled = true }
  }, [workspace?.id, profile?.id, routeKind, routeKey, resolveBump])

  const activeChannelId = channelState.kind === 'ready' ? channelState.channelId : null

  // ─── Messages query (polled) ──────────────────────────────────────────────
  const { data: messages = [] } = useQuery<TeamMessage[]>({
    queryKey: ['team-messages', activeChannelId],
    enabled: !!activeChannelId,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      if (!activeChannelId) return []
      const { data, error } = await supabase
        .from('team_messages')
        .select('id, team_conversation_id, user_id, content, created_at, edited_at, deleted_at, attachments, parent_message_id, profiles!team_messages_user_fkey(full_name)')
        .eq('team_conversation_id', activeChannelId)
        .is('parent_message_id', null)   // main channel list = top-level only; replies live in the thread panel (CP2)
        .order('created_at', { ascending: false })
        .limit(MAX_MESSAGES)
      if (error) throw error
      return ((data || []) as any[]).reverse() as TeamMessage[]
    },
  })

  // ─── Reactions query (sibling to messages; low-volume, polled + realtime) ──
  const { data: reactions = [] } = useQuery<Reaction[]>({
    queryKey: ['message-reactions', activeChannelId],
    enabled: !!activeChannelId,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      if (!activeChannelId) return []
      const { data, error } = await supabase
        .from('message_reactions')
        .select('id, team_message_id, user_id, emoji')
        .eq('team_conversation_id', activeChannelId)
      if (error) throw error
      return (data || []) as Reaction[]
    },
  })
  const reactionsByMessage = useMemo(() => {
    const map: Record<string, Reaction[]> = {}
    for (const r of reactions) { (map[r.team_message_id] ||= []).push(r) }
    return map
  }, [reactions])

  // ─── Thread reply counts (one grouped query → per-root count; no N+1) ──────
  const { data: replyCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ['thread-reply-counts', activeChannelId],
    enabled: !!activeChannelId,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      if (!activeChannelId) return {}
      const { data, error } = await supabase
        .from('team_messages')
        .select('parent_message_id')
        .eq('team_conversation_id', activeChannelId)
        .not('parent_message_id', 'is', null)
        .is('deleted_at', null)   // count live replies only
      if (error) throw error
      const map: Record<string, number> = {}
      for (const r of (data || []) as any[]) { const p = r.parent_message_id as string | null; if (p) map[p] = (map[p] || 0) + 1 }
      return map
    },
  })
  // Which root the thread drawer is open for (null = closed).
  const [threadRootId, setThreadRootId] = useState<string | null>(null)

  // ─── Realtime subscription (scoped to active conversation) ────────────────
  useEffect(() => {
    if (!activeChannelId) return
    const channel = supabase
      .channel(`team-messages:${activeChannelId}`)
      .on(
        'postgres_changes',
        {
          event: '*',   // INSERT (append) + UPDATE (edit / soft-delete → patch-by-id)
          schema: 'public',
          table: 'team_messages',
          filter: `team_conversation_id=eq.${activeChannelId}`,
        },
        (payload) => {
          const incoming = payload.new as (Partial<TeamMessage> & { id: string; user_id: string | null; content: string; created_at: string; team_conversation_id: string }) | undefined
          if (!incoming?.id) return
          // ─── Reply routing: a row with parent_message_id belongs to a THREAD,
          //     not the main list. Refresh the open thread (if any) + the reply
          //     count; never append it to the channel list (would double-render).
          const parentId = (incoming as any).parent_message_id as string | null | undefined
          if (parentId) {
            queryClient.invalidateQueries({ queryKey: ['thread-messages', parentId] })
            queryClient.invalidateQueries({ queryKey: ['thread-reply-counts', activeChannelId] })
            return
          }
          queryClient.setQueryData<TeamMessage[]>(
            ['team-messages', activeChannelId],
            (old) => {
              const arr = old || []
              const existing = arr.find(m => m.id === incoming.id)
              const profiles =
                incoming.user_id && profile?.id && incoming.user_id === profile.id && profile.full_name
                  ? { full_name: profile.full_name }
                  : existing?.profiles ?? null
              const enriched: TeamMessage = {
                id: incoming.id,
                team_conversation_id: incoming.team_conversation_id,
                user_id: incoming.user_id,
                content: incoming.content,
                created_at: incoming.created_at,
                edited_at: incoming.edited_at ?? null,
                deleted_at: incoming.deleted_at ?? null,
                attachments: ((incoming as any).attachments as MessageAttachment[] | undefined) ?? existing?.attachments ?? [],
                parent_message_id: ((incoming as any).parent_message_id as string | null | undefined) ?? existing?.parent_message_id ?? null,
                profiles,
              }
              // UPDATE → replace the row in place; INSERT → append (dedup).
              if (existing) return arr.map(m => m.id === incoming.id ? enriched : m)
              return [...arr, enriched]
            },
          )
        },
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn(`[team-chat] realtime status: ${status} — polling fallback active`)
        }
      })

    return () => { supabase.removeChannel(channel) }
  }, [activeChannelId, queryClient, profile?.id, profile?.full_name])

  // ─── Reactions realtime (INSERT + DELETE) — invalidate on any change ──────
  useEffect(() => {
    if (!activeChannelId) return
    const channel = supabase
      .channel(`message-reactions:${activeChannelId}`)
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'message_reactions',
        filter: `team_conversation_id=eq.${activeChannelId}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['message-reactions', activeChannelId] })
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [activeChannelId, queryClient])

  // ─── Mark as read on conversation-switch ──────────────────────────────────
  useEffect(() => {
    if (!activeChannelId || !profile?.id) return
    supabase
      .from('team_conversation_participants')
      .update({ last_read_at: new Date().toISOString() })
      .eq('team_conversation_id', activeChannelId)
      .eq('user_id', profile.id)
      .then(({ error }) => {
        if (error) console.warn('[team-chat] mark-read failed:', error.message)
        else {
          queryClient.invalidateQueries({ queryKey: ['team-joined-channels'] })
          queryClient.invalidateQueries({ queryKey: ['team-joined-dms-groups'] })
          queryClient.invalidateQueries({ queryKey: ['team-chat-unread'] })
        }
      })
  }, [activeChannelId, profile?.id, queryClient])

  // ─── Send message ─────────────────────────────────────────────────────────
  const sendMutation = useMutation({
    mutationFn: async ({ content, attachments }: { content: string; attachments: MessageAttachment[] }) => {
      if (!activeChannelId || !profile?.id) throw new Error('Not ready')
      const trimmed = content.trim()
      if (!trimmed && attachments.length === 0) throw new Error('Empty')
      const { data, error } = await supabase
        .from('team_messages')
        .insert({
          team_conversation_id: activeChannelId,
          user_id: profile.id,
          content: trimmed.slice(0, MAX_CONTENT_LEN),
          attachments,
        })
        .select('id').single()
      if (error) throw error
      // parse-on-send: extract @[Name](id) tokens → message_mentions rows. The CP1
      // trigger fires the bell (skip-self handled DB-side). A failed mention insert
      // must not lose the message — log and move on.
      const mentions = parseMentions(trimmed)
      if (data?.id && mentions.length) {
        const rows = mentions.map(mn => ({
          team_message_id: data.id,
          team_conversation_id: activeChannelId,
          mentioned_user_id: mn.userId,
          mentioning_user_id: profile.id,
        }))
        const { error: mErr } = await (supabase as any).from('message_mentions').insert(rows)
        if (mErr) console.warn('[mentions] insert failed', mErr.message)
      }
    },
    onSuccess: () => {
      setInput('')
      setPendingAttachment(null)
      setShowMentions(false)
      queryClient.invalidateQueries({ queryKey: ['team-messages', activeChannelId] })
      textareaRef.current?.focus()
    },
  })

  // ─── @-mention autocomplete (conversation members) ────────────────────────
  const [showMentions, setShowMentions] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [mentionIndex, setMentionIndex] = useState(0)
  const { data: convMembers = [] } = useQuery({
    queryKey: ['team-members', activeChannelId],
    enabled: !!activeChannelId,
    queryFn: async () => {
      if (!activeChannelId) return []
      const { data } = await supabase
        .from('team_conversation_participants')
        .select('user_id, profiles!team_conversation_participants_user_fkey(id, full_name)')
        .eq('team_conversation_id', activeChannelId)
      return (data || []).map((r: any) => ({ id: r.user_id as string, fullName: (r.profiles?.full_name as string | null) || 'Unknown user' }))
    },
  })
  const mentionMatches = useMemo(() => {
    const q = mentionQuery.toLowerCase()
    return (convMembers as { id: string; fullName: string }[])
      .filter(m => m.fullName.toLowerCase().includes(q)).slice(0, 6)
  }, [convMembers, mentionQuery])

  const handleComposerChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value.slice(0, MAX_CONTENT_LEN)
    setInput(val)
    const caret = e.target.selectionStart ?? val.length
    const m = val.slice(0, caret).match(/@(\w*)$/)
    if (m) { setShowMentions(true); setMentionQuery(m[1]); setMentionIndex(0) }
    else setShowMentions(false)
  }
  const selectMention = (mb: { id: string; fullName: string }) => {
    const ta = textareaRef.current
    const caret = ta?.selectionStart ?? input.length
    const { value, caret: newCaret } = insertMentionToken(input, caret, mb.fullName, mb.id)
    setInput(value)
    setShowMentions(false)
    requestAnimationFrame(() => { if (ta) { ta.focus(); ta.setSelectionRange(newCaret, newCaret) } })
  }

  // ─── Edit / soft-delete own messages ──────────────────────────────────────
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const afterMsgWrite = () => queryClient.invalidateQueries({ queryKey: ['team-messages', activeChannelId] })

  const editMutation = useMutation({
    mutationFn: async ({ id, content }: { id: string; content: string }) => {
      if (!profile?.id) throw new Error('Not ready')
      const trimmed = content.trim()
      if (!trimmed) throw new Error('Empty')
      const { error } = await supabase.from('team_messages')
        .update({ content: trimmed.slice(0, MAX_CONTENT_LEN), edited_at: new Date().toISOString() })
        .eq('id', id).eq('user_id', profile.id)   // RLS: own only (defense in depth)
      if (error) throw error
    },
    onSuccess: () => { setEditingId(null); setEditDraft(''); afterMsgWrite() },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!profile?.id) throw new Error('Not ready')
      const { error } = await supabase.from('team_messages')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id).eq('user_id', profile.id)
      if (error) throw error
    },
    onSuccess: () => { setMenuFor(null); afterMsgWrite() },
  })

  // ─── Composer attachment (single file, upload-on-pick) ─────────────────────
  const [pendingAttachment, setPendingAttachment] = useState<MessageAttachment | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handlePickFile = async (file: File) => {
    if (!workspace?.id || !activeChannelId) return
    if (file.size > MAX_ATTACHMENT_BYTES) { toast.error('File is too large (max 15MB)'); return }
    setUploading(true)
    const { data, error } = await uploadMessageAttachment(file, workspace.id, activeChannelId)
    setUploading(false)
    if (error || !data) { toast.error(error?.message || 'Upload failed'); return }
    setPendingAttachment(data)
  }

  const handleRemoveAttachment = () => {
    // Orphan-cleanup: the object was uploaded on pick but never referenced by a
    // message, so remove it from storage when the user discards it.
    if (pendingAttachment) deleteMessageAttachment(pendingAttachment.path).catch(() => {})
    setPendingAttachment(null)
  }

  const canSend = (!!input.trim() || !!pendingAttachment) && !uploading && !sendMutation.isPending && !!activeChannelId
  const handleSend = () => {
    if (canSend) sendMutation.mutate({ content: input, attachments: pendingAttachment ? [pendingAttachment] : [] })
  }

  // ─── Auto-scroll ──────────────────────────────────────────────────────────
  const lastCountRef = useRef(0)
  useEffect(() => {
    if (messages.length !== lastCountRef.current) {
      lastCountRef.current = messages.length
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      })
    }
  }, [messages.length])

  useEffect(() => { lastCountRef.current = 0 }, [activeChannelId])

  const displayMessages = useMemo(() => messages.map(m => ({
    ...m,
    senderName: m.profiles?.full_name || 'Unknown user',
    displayTime: formatMessageTime(m.created_at),
  })), [messages])

  // ─── Join action (channels only) ──────────────────────────────────────────
  const handleJoin = async () => {
    if (channelState.kind !== 'not-member-channel' || !profile?.id) return
    const { error } = await supabase
      .from('team_conversation_participants')
      .insert({ team_conversation_id: channelState.channelId, user_id: profile.id })
    if (error) {
      console.warn('[team-chat] join failed:', error.message)
      return
    }
    queryClient.invalidateQueries({ queryKey: ['team-joined-channels'] })
    queryClient.invalidateQueries({ queryKey: ['team-chat-unread'] })
    setResolveBump(b => b + 1)
  }

  // ─── Sidebar active route ─────────────────────────────────────────────────
  const sidebarActiveRoute: SidebarActiveRoute =
    channelState.kind === 'not-found'
      ? null
      : routeKind === 'channel'
        ? { kind: 'channel', name: routeKey }
        : { kind: routeKind, id: routeKey }

  // ─── Header helpers ───────────────────────────────────────────────────────
  const headerTitle =
    channelState.kind === 'ready' ? channelState.displayName :
    channelState.kind === 'not-member-channel' ? `#${channelState.channelName}` :
    channelState.kind === 'not-found' ? 'Conversation not found' :
    routeKind === 'channel' ? `#${routeKey}` : ''

  const headerSubtitle =
    channelState.kind === 'ready' ? channelState.subtitle :
    workspace?.name || ''

  const composerPlaceholder =
    channelState.kind === 'ready'
      ? channelState.convType === 'channel'
        ? `Message ${channelState.displayName}…`
        : channelState.convType === 'dm'
          ? `Message ${channelState.avatarName || 'them'}…`
          : 'Message the group…'
      : 'Message…'

  return (
    <div style={{ height: '100%', display: 'flex', background: 'var(--canvas)', overflow: 'hidden', position: 'relative' }}>
      {/* Mobile drawer backdrop. Dims the chat behind the channel list and
          dismisses on tap. Rendered only when isMobile && mobileSidebarOpen
          so it never affects desktop. */}
      {isMobile && mobileSidebarOpen && (
        <div
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, zIndex: 40, background: 'var(--overlay-scrim)' }}
        />
      )}

      {/* Channel sidebar. Desktop: inline flex child (unchanged). Mobile:
          fixed-position overlay that slides in via translateX when the
          hamburger is tapped. The style-override prop lets us re-shape
          the sidebar to full-drawer-width without forking the component. */}
      {isMobile ? (
        <motion.div
          initial={false}
          animate={{ x: mobileSidebarOpen ? 0 : -300 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          style={{ position: 'absolute', top: 0, left: 0, height: '100%', zIndex: 50, width: 280, display: 'flex' }}
        >
          <ChannelSidebar
            activeRoute={sidebarActiveRoute}
            onChannelSelect={name => navigate(`/app/team-chat/${name}`)}
            onDMSelect={id => navigate(`/app/team-chat/dm/${id}`)}
            onGroupSelect={id => navigate(`/app/team-chat/group/${id}`)}
            onBrowseChannels={() => setShowDirectory(true)}
            onCreateChannel={() => setShowDirectory(true)}
            onStartDM={() => setShowStartDM(true)}
            onNewGroup={() => setShowNewGroup(true)}
            style={{ width: '100%', borderRight: 'none', boxShadow: 'var(--popup-shadow)' }}
          />
        </motion.div>
      ) : (
        <ChannelSidebar
          activeRoute={sidebarActiveRoute}
          onChannelSelect={name => navigate(`/app/team-chat/${name}`)}
          onDMSelect={id => navigate(`/app/team-chat/dm/${id}`)}
          onGroupSelect={id => navigate(`/app/team-chat/group/${id}`)}
          onBrowseChannels={() => setShowDirectory(true)}
          onCreateChannel={() => setShowDirectory(true)}
          onStartDM={() => setShowStartDM(true)}
          onNewGroup={() => setShowNewGroup(true)}
        />
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ padding: '18px 28px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {/* Mobile-only hamburger to open the channel drawer. */}
          {isMobile && (
            <button
              type="button"
              onClick={() => setMobileSidebarOpen(o => !o)}
              aria-label={mobileSidebarOpen ? 'Close channels' : 'Open channels'}
              aria-expanded={mobileSidebarOpen}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-primary)', padding: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, flexShrink: 0 }}
            >
              <Menu style={{ width: 18, height: 18 }} />
            </button>
          )}
          {channelState.kind === 'ready' && channelState.convType === 'dm' ? (
            <Avatar name={channelState.avatarName || 'User'} size={28} variant="contact" online={!!channelState.dmOtherId && onlineUserIds.has(channelState.dmOtherId)} />
          ) : channelState.kind === 'ready' && channelState.convType === 'group' ? (
            <UsersIcon style={{ width: 18, height: 18, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
          ) : (
            <Hash style={{ width: 18, height: 18, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {headerTitle}
            </h1>
            {headerSubtitle && (
              <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '3px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {headerSubtitle}
              </p>
            )}
          </div>
          {channelState.kind === 'ready'
            && channelState.convType === 'dm'
            && channelState.dmOtherId && (
              <button
                type="button"
                onClick={() => startCall(channelState.dmOtherId as string, channelState.displayName)}
                disabled={callBusy}
                aria-label={`Call ${channelState.displayName}`}
                title={callBusy ? 'You’re already on a call' : `Call ${channelState.displayName}`}
                style={{
                  marginLeft: 'auto',
                  background: 'none', border: 'none',
                  cursor: callBusy ? 'not-allowed' : 'pointer',
                  color: 'var(--ink-primary)', padding: 6,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 6, flexShrink: 0,
                  opacity: callBusy ? 0.4 : 1,
                }}
              >
                <Phone style={{ width: 18, height: 18 }} />
              </button>
            )}
          {channelState.kind === 'ready'
            && channelState.convType === 'group' && (
              <button
                type="button"
                onClick={() => { void startGroupCall(channelState.channelId) }}
                disabled={callBusy}
                aria-label="Start group call"
                title={callBusy ? 'You’re already on a call' : 'Start group call'}
                style={{
                  marginLeft: 'auto',
                  background: 'none', border: 'none',
                  cursor: callBusy ? 'not-allowed' : 'pointer',
                  color: 'var(--ink-primary)', padding: 6,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: 6, flexShrink: 0,
                  opacity: callBusy ? 0.4 : 1,
                }}
              >
                <Phone style={{ width: 18, height: 18 }} />
              </button>
            )}
          <button
            type="button"
            onClick={() => setShowSearch(true)}
            aria-label="Search messages"
            title="Search messages"
            style={{
              marginLeft: channelState.kind === 'ready' && (channelState.convType === 'dm' || channelState.convType === 'group') ? 0 : 'auto',
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--ink-primary)', padding: 6,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 6, flexShrink: 0,
            }}
          >
            <Search style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {channelState.kind === 'loading' && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-tertiary)' }}>
            <Loader2 style={{ width: 16, height: 16, marginRight: 8 }} className="animate-spin" />
            <span style={{ fontSize: 13 }}>Loading…</span>
          </div>
        )}

        {channelState.kind === 'not-found' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 }}>
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: 0 }}>
              {routeKind === 'channel' ? 'Channel not found' : 'Conversation not available'}
            </p>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '6px 0 16px' }}>
              {routeKind === 'channel'
                ? `No channel named #${routeKey} in this workspace.`
                : 'This conversation either does not exist or you are not a participant.'}
            </p>
            <button
              onClick={() => navigate('/app/team-chat/general')}
              className="btn-primary"
            >
              Back to #general
            </button>
          </div>
        )}

        {channelState.kind === 'not-member-channel' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 }}>
            <Hash style={{ width: 32, height: 32, color: 'var(--ink-tertiary)', marginBottom: 12 }} />
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: 0 }}>You're not a member of #{channelState.channelName}</p>
            {channelState.description && (
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '6px 0 0', maxWidth: 360 }}>{channelState.description}</p>
            )}
            <button
              onClick={handleJoin}
              className="btn-primary"
              style={{ marginTop: 16 }}
            >
              <LogIn style={{ width: 14, height: 14 }} /> Join channel
            </button>
          </div>
        )}

        {channelState.kind === 'ready' && (
          <>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 28px 12px' }}>
              {displayMessages.length === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', color: 'var(--ink-tertiary)' }}>
                  <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink-secondary)', margin: 0 }}>No messages yet.</p>
                  <p style={{ fontSize: 13, margin: '6px 0 0' }}>
                    {channelState.convType === 'channel'
                      ? `Say hello to ${channelState.displayName}!`
                      : channelState.convType === 'dm'
                        ? `Say hello to ${channelState.avatarName || 'them'}!`
                        : 'Start the conversation!'}
                  </p>
                </div>
              )}

              {displayMessages.map(m => {
                const isDeleted = !!m.deleted_at
                const isMine = !!m.user_id && m.user_id === profile?.id
                const isEditing = editingId === m.id
                return (
                <div key={m.id} onMouseLeave={() => setMenuFor(null)}
                  style={{ display: 'flex', gap: 12, padding: '8px 0', alignItems: 'flex-start', position: 'relative' }}>
                  <Avatar name={m.senderName} size={36} variant="contact" online={!!m.user_id && onlineUserIds.has(m.user_id)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>
                        {m.senderName}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>
                        {m.displayTime}
                      </span>
                      {!isDeleted && m.edited_at && (
                        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>(edited)</span>
                      )}
                    </div>

                    {isDeleted ? (
                      <div style={{ fontSize: 14, color: 'var(--ink-tertiary)', fontStyle: 'italic', marginTop: 2 }}>
                        This message was deleted
                      </div>
                    ) : isEditing ? (
                      <div style={{ marginTop: 4 }}>
                        <textarea value={editDraft} onChange={e => setEditDraft(e.target.value)} autoFocus rows={2}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); editMutation.mutate({ id: m.id, content: editDraft }) }
                            else if (e.key === 'Escape') { setEditingId(null); setEditDraft('') }
                          }}
                          style={{ width: '100%', padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8, fontSize: 14, color: 'var(--ink-primary)', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
                        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                          <button type="button" onClick={() => editMutation.mutate({ id: m.id, content: editDraft })} disabled={!editDraft.trim() || editMutation.isPending}
                            style={{ padding: '4px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, background: 'var(--accent)', color: 'var(--accent-ink)', border: 'none', cursor: 'pointer' }}>Save</button>
                          <button type="button" onClick={() => { setEditingId(null); setEditDraft('') }}
                            style={{ padding: '4px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, background: 'var(--hover-bg)', color: 'var(--ink-secondary)', border: '1px solid var(--border-subtle)', cursor: 'pointer' }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 14, color: 'var(--ink-secondary)', lineHeight: 1.5, marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {renderWithMentions(m.content)}
                      </div>
                    )}

                    {!isDeleted && !isEditing && m.attachments?.length > 0 && (
                      <MessageAttachmentList attachments={m.attachments} />
                    )}

                    {activeChannelId && !isDeleted && !isEditing && (
                      <MessageReactions messageId={m.id} conversationId={activeChannelId}
                        reactions={reactionsByMessage[m.id] || []} currentUserId={profile?.id ?? null} />
                    )}

                    {!isDeleted && !isEditing && (replyCounts[m.id] || 0) > 0 && (
                      <button type="button" onClick={() => setThreadRootId(m.id)}
                        style={{ marginTop: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, padding: '2px 0', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {replyCounts[m.id]} {replyCounts[m.id] === 1 ? 'reply' : 'replies'}
                      </button>
                    )}
                  </div>

                  {/* Message actions kebab → Reply in thread (any message) + Edit/Delete (own only) */}
                  {!isDeleted && !isEditing && (
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <button type="button" aria-label="Message actions" onClick={() => setMenuFor(menuFor === m.id ? null : m.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', borderRadius: 6 }}>
                        <MoreHorizontal style={{ width: 16, height: 16 }} />
                      </button>
                      {menuFor === m.id && (
                        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 2, background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 9, padding: 4, zIndex: 10, minWidth: 140 }}>
                          <button type="button" onClick={() => { setThreadRootId(m.parent_message_id ?? m.id); setMenuFor(null) }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--ink-primary)', borderRadius: 6 }}>Reply in thread</button>
                          {isMine && (
                            <>
                              <button type="button" onClick={() => { setEditingId(m.id); setEditDraft(m.content); setMenuFor(null) }}
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--ink-primary)', borderRadius: 6 }}>Edit</button>
                              <button type="button" onClick={() => { if (window.confirm('Delete this message?')) deleteMutation.mutate(m.id) }}
                                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--danger)', borderRadius: 6 }}>Delete</button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )
              })}

              <div ref={messagesEndRef} />
            </div>

            <div style={{ padding: '12px 28px 18px', borderTop: '1px solid var(--border-subtle)', flexShrink: 0, position: 'relative' }}>
              {/* @-mention dropdown — mirrors the inbox '/' canned-response picker. */}
              <AnimatePresence>
                {showMentions && mentionMatches.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                    style={{ position: 'absolute', bottom: '100%', left: 28, right: 28, marginBottom: 8, background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 12, overflow: 'hidden', zIndex: 50, maxHeight: 240 }}>
                    <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)' }}>Mention a teammate · ↑↓ then Enter</div>
                    <div style={{ maxHeight: 200, overflow: 'auto' }}>
                      {mentionMatches.map((mb, i) => (
                        <button key={mb.id} type="button" onMouseDown={e => { e.preventDefault(); selectMention(mb) }}
                          onMouseEnter={() => setMentionIndex(i)}
                          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: i === mentionIndex ? 'var(--accent-dim)' : 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                          <Avatar name={mb.fullName} size={24} variant="contact" />
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{mb.fullName}</span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <DraftMentionPills content={input} onRemove={uid => setInput(removeMentionToken(input, uid))} style={{ marginBottom: 8 }} />
              {/* Pending attachment chip (uploaded on pick; removable before send) */}
              {(pendingAttachment || uploading) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '6px 10px', background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 10, maxWidth: 320 }}>
                  {uploading ? (
                    <><Loader2 style={{ width: 14, height: 14, color: 'var(--ink-tertiary)' }} className="animate-spin" /><span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>Uploading…</span></>
                  ) : pendingAttachment && (
                    <>
                      <Paperclip style={{ width: 14, height: 14, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)' }}>{pendingAttachment.name}</span>
                      <button type="button" onClick={handleRemoveAttachment} aria-label="Remove attachment" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 2, display: 'flex', flexShrink: 0 }}>
                        <X style={{ width: 14, height: 14 }} />
                      </button>
                    </>
                  )}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACHMENT_ACCEPT_ATTR}
                onChange={e => { const f = e.target.files?.[0]; if (f) handlePickFile(f); e.target.value = '' }}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 12, padding: '8px 10px' }}>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || !!pendingAttachment || sendMutation.isPending}
                  aria-label="Attach file"
                  title={pendingAttachment ? 'Remove the current attachment first' : 'Attach a file'}
                  style={{ background: 'none', border: 'none', cursor: (uploading || pendingAttachment) ? 'not-allowed' : 'pointer', color: 'var(--ink-tertiary)', padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, flexShrink: 0, opacity: (uploading || pendingAttachment) ? 0.4 : 1 }}
                >
                  <Paperclip style={{ width: 16, height: 16 }} />
                </button>
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={handleComposerChange}
                  onKeyDown={e => {
                    if (showMentions && mentionMatches.length > 0) {
                      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(i => (i + 1) % mentionMatches.length); return }
                      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(i => (i - 1 + mentionMatches.length) % mentionMatches.length); return }
                      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectMention(mentionMatches[mentionIndex]); return }
                      if (e.key === 'Escape') { e.preventDefault(); setShowMentions(false); return }
                    }
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSend()
                    }
                  }}
                  placeholder={composerPlaceholder}
                  rows={3}
                  disabled={sendMutation.isPending}
                  style={{
                    flex: 1,
                    minHeight: 60,
                    maxHeight: 144,
                    resize: 'none',
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: 'var(--ink-primary)',
                    fontSize: 14,
                    lineHeight: 1.5,
                    fontFamily: 'inherit',
                    padding: '6px 4px',
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={!canSend}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: 'none',
                    cursor: canSend ? 'pointer' : 'not-allowed',
                    background: canSend ? 'var(--accent)' : 'var(--hover-bg)',
                    color: canSend ? 'var(--accent-ink)' : 'var(--ink-tertiary)',
                    fontSize: 13,
                    fontWeight: 600,
                    gap: 6,
                    flexShrink: 0,
                    transition: 'background 150ms ease',
                  }}
                  title="Send (Enter)"
                >
                  {sendMutation.isPending ? (
                    <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
                  ) : (
                    <Send style={{ width: 14, height: 14 }} />
                  )}
                  <span>Send</span>
                </button>
              </div>
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '8px 2px 0' }}>
                Enter to send · Shift+Enter for new line
              </p>
            </div>
          </>
        )}
      </div>

      <AnimatePresence>
        {showDirectory && (
          <ChannelDirectory
            onClose={() => setShowDirectory(false)}
            onChannelJoin={name => { setShowDirectory(false); navigate(`/app/team-chat/${name}`) }}
            onChannelCreate={name => { setShowDirectory(false); navigate(`/app/team-chat/${name}`) }}
          />
        )}
        {showStartDM && (
          <StartDMModal
            onClose={() => setShowStartDM(false)}
            onDMOpen={id => { setShowStartDM(false); navigate(`/app/team-chat/dm/${id}`) }}
          />
        )}
        {showNewGroup && (
          <NewGroupModal
            onClose={() => setShowNewGroup(false)}
            onGroupCreate={id => { setShowNewGroup(false); navigate(`/app/team-chat/group/${id}`) }}
          />
        )}
        {threadRootId && activeChannelId && workspace?.id && profile?.id && (
          <ThreadDrawer
            rootId={threadRootId}
            conversationId={activeChannelId}
            workspaceId={workspace.id}
            currentUserId={profile.id}
            currentUserName={profile.full_name ?? null}
            reactionsByMessage={reactionsByMessage}
            convMembers={convMembers as { id: string; fullName: string }[]}
            onlineUserIds={onlineUserIds}
            onClose={() => setThreadRootId(null)}
          />
        )}
        {showSearch && (
          <SearchMessagesModal
            onClose={() => setShowSearch(false)}
            onSelect={route => { setShowSearch(false); navigate(route) }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
